// Admin Channel Partner service.
// A channel partner is a `users` row with role = 'channel_partner'. Admin
// creates/manages the account here; the partner then logs in through the
// normal phone+OTP login page like any other user, and lands on the same
// dashboard used for auction creation/management.
//
// Endpoints:
//  GET    /api/admin/channel-partners
//  GET    /api/admin/channel-partners/:id
//  POST   /api/admin/channel-partners
//  PUT    /api/admin/channel-partners/:id
//  PUT    /api/admin/channel-partners/:id/active   (body { is_active: boolean })
//  POST   /api/admin/channel-partners/promote/:userId  (makes an existing user a CP, keeps the user account)
//  DELETE /api/admin/channel-partners/:id          (revokes CP status, keeps the user account)

import { API_BASE_URL } from './apiConfig';

const BASE = `${API_BASE_URL}/admin/channel-partners`;

export interface ChannelPartner {
  id: number;
  company_name: string;
  phone_number: string;
  person_name: string;
  email: string | null;
  company_address: string | null;
  company_product_service: string | null;
  role: 'channel_partner';
  is_active: boolean | number;
  is_approved: boolean | number;
  status: string;
  auctions_created: number;
  created_at?: string;
  updated_at?: string;
  stats?: {
    total_auctions: number;
    live_auctions: number;
    completed_auctions: number;
    upcoming_auctions: number;
  };
}

export interface ChannelPartnerInput {
  company_name: string;
  phone_number: string;
  person_name: string;
  email?: string;
  company_address?: string;
  company_product_service?: string;
}

export type NotificationStatus = 'sent' | 'failed' | 'skipped_no_email' | null;

export interface CreatePartnerResult {
  partner: ChannelPartner;
  notifications: { sms: NotificationStatus; email: NotificationStatus };
}

function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const authToken = localStorage.getItem('authToken');
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.success === false) {
    throw new Error(body?.message || `Request failed (${res.status})`);
  }
  return body.data as T;
}

class AdminChannelPartnerService {
  async getAll(): Promise<ChannelPartner[]> {
    const res = await fetch(BASE, { headers: getAuthHeaders() });
    return parseResponse<ChannelPartner[]>(res);
  }

  async getById(id: number): Promise<ChannelPartner> {
    const res = await fetch(`${BASE}/${id}`, { headers: getAuthHeaders() });
    return parseResponse<ChannelPartner>(res);
  }

  async create(input: ChannelPartnerInput): Promise<CreatePartnerResult> {
    const res = await fetch(BASE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body || body.success === false) {
      throw new Error(body?.message || `Request failed (${res.status})`);
    }
    return { partner: body.data, notifications: body.notifications || { sms: null, email: null } };
  }

  async update(id: number, input: Partial<ChannelPartnerInput>): Promise<ChannelPartner> {
    const res = await fetch(`${BASE}/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });
    return parseResponse<ChannelPartner>(res);
  }

  async setActive(id: number, isActive: boolean): Promise<ChannelPartner> {
    const res = await fetch(`${BASE}/${id}/active`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ is_active: isActive }),
    });
    return parseResponse<ChannelPartner>(res);
  }

  // Turns an existing regular user into a channel partner. Their account and
  // history are untouched; the same login now also shows the CP features.
  async promote(userId: number | string): Promise<ChannelPartner> {
    const res = await fetch(`${BASE}/promote/${userId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return parseResponse<ChannelPartner>(res);
  }

  async revoke(id: number | string): Promise<void> {
    const res = await fetch(`${BASE}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await parseResponse<void>(res);
  }
}

export default new AdminChannelPartnerService();
